import { ReportFileMetadataUsageEnum } from '@nimbly-technologies/nimbly-common/lib/enumerators/reportFileMetadata';
import { ReportFileTypeEnum } from '@nimbly-technologies/nimbly-common/lib/enumerators/reportFileType';

import { NIMBLY_API_ATTACHMENT_SIGNED_URL } from '../constants/api';
import API from '../store/helpers/api';

export { ReportFileTypeEnum, ReportFileMetadataUsageEnum };

export type CreateReportAttachmentSignedUrlRequest = {
  fileName: string;
  fileType: ReportFileTypeEnum;
  mimeType: string;
  size: number;
  reportID: string;
  siteID: string;
  usageType: ReportFileMetadataUsageEnum;
  questionIndex: number;
  childQuestionIndex: string;
};

export async function createReportAttachmentSignedUrl(
  body: CreateReportAttachmentSignedUrlRequest
): Promise<{ signedURL: string; filePath: string }> {
  const api = await API.withToken();
  const json = await api.postJSON(NIMBLY_API_ATTACHMENT_SIGNED_URL, body);

  if (!json?.data?.signedURL || !json?.data?.filePath) {
    throw new Error('createReportAttachmentSignedUrl: missing signedURL or filePath in response');
  }

  return { signedURL: json.data.signedURL, filePath: json.data.filePath };
}

export async function uploadBinaryToSignedUrl({
  signedURL,
  file,
  mimeType,
  signal
}: {
  signedURL: string;
  file: Blob | File;
  mimeType: string;
  signal?: AbortSignal;
}): Promise<void> {
  const res = await fetch(signedURL, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': mimeType },
    signal
  });

  if (!res.ok) {
    throw new Error(`uploadBinaryToSignedUrl: PUT failed with status ${res.status}`);
  }
}

export async function resolveReportAttachmentDownloadUrl(filePath: string): Promise<string> {
  if (filePath.startsWith('https://') || filePath.startsWith('http://')) {
    return filePath;
  }

  const api = await API.withToken();
  const json = await api.getJSONOrThrow(`${NIMBLY_API_ATTACHMENT_SIGNED_URL}?filePath=${encodeURIComponent(filePath)}`);

  if (!json?.data?.signedURL) {
    throw new Error('resolveReportAttachmentDownloadUrl: missing signedURL in response');
  }

  return json.data.signedURL;
}

// Converts string (data URL, blob URL, http URL) or Blob/File to Blob.
export async function normalizeToBlob(input: File | Blob | string): Promise<Blob> {
  if (input instanceof Blob) {
    return input;
  }

  const res = await fetch(input);
  if (!res.ok) {
    throw new Error(`normalizeToBlob: fetch failed with status ${res.status}`);
  }

  return res.blob();
}

// Compose: create signed URL + PUT binary. Returns backend filePath.
export async function uploadReportAttachment({
  body,
  file,
  signal
}: {
  body: Omit<CreateReportAttachmentSignedUrlRequest, 'size'>;
  file: File | Blob | string;
  signal?: AbortSignal;
}): Promise<string> {
  const blob = await normalizeToBlob(file);
  const { signedURL, filePath } = await createReportAttachmentSignedUrl({
    ...body,
    size: blob.size
  });

  await uploadBinaryToSignedUrl({ signedURL, file: blob, mimeType: body.mimeType, signal });

  return filePath;
}
